import React, { useState, useEffect } from "react"
import { Form, DatePicker, Select, Input, Button, TimePicker, message } from "antd"
import { Plus } from "lucide-react"
import moment from "moment"
import { secureApi } from "../../Config/api"
import { GET, POST } from "../../utils/apis"
import { useSelector } from "react-redux"
import { translations } from "../../utils/constants"
import { convertLocalToItalianTime } from "../../utils/helpers"

const { Option } = Select
const { RangePicker } = DatePicker
const { TextArea } = Input

const ApplyLeave = ({ language, onSuccess, onCancel }) => {
    const user = useSelector((state) => state.authReducer?.user)
    const userId = user?.id
    const employeeName = user?.userName
    const [form] = Form.useForm()
    const [leaveType, setLeaveType] = useState("vacation")
    const [halfDay, setHalfDay] = useState(false)
    const [disabledDates, setDisabledDates] = useState([])

    useEffect(() => {
        const fetchLeaveDates = async () => {
            try {
                const res = await secureApi.get(`${GET.GET_APPROVED_LEAVE_DATES}?user_id=${userId}`)
                // console.log(res,'res')
                if (res?.success) {
                    const allDates = res.allDates || []
                    const normalized = allDates.map(d => moment(d).format("YYYY-MM-DD"))
                    setDisabledDates(normalized)
                }
            } catch (err) {
                console.error("Error fetching leave dates:", err)
            }
        }
        if (userId) fetchLeaveDates()
    }, [userId])

    const isDateDisabled = (current) => {
        if (!current) return false
        const formatted = current.format("YYYY-MM-DD")
        return current < moment().startOf("day") || disabledDates.includes(formatted)
    }

    // const onFinish = async (values) => {
    //     const payload = {
    //         user_id: userId,
    //         employeeName,
    //         type: leaveType,
    //         reason: values.reason,
    //     }

    //     if (leaveType === "vacation") {
    //         payload.startDate = values.dateRange?.[0]?.format("YYYY-MM-DD")
    //         payload.endDate = values.dateRange?.[1]?.format("YYYY-MM-DD")
    //     } else {
    //         payload.date = values.date?.format("YYYY-MM-DD")
    //         payload.fullDay = !halfDay
    //         if (halfDay) {
    //             payload.startTime = values.timeRange?.[0]?.format("HH:mm")
    //             payload.endTime = values.timeRange?.[1]?.format("HH:mm")
    //         }
    //     }

    //     try {
    //         const response = await secureApi.post(POST?.APPLY_LEAVE, payload)
    //         if (response?.success) {
    //             message.success(translations[language]?.leaveAppliedSuccess)
    //             form.resetFields()
    //             setHalfDay(false)
    //             if (typeof onSuccess === "function") onSuccess()
    //         } else {
    //             message.error(response?.message || translations[language]?.leaveApplyError)
    //         }
    //     } catch {
    //         message.error(translations[language]?.leaveSubmitError)
    //     }
    // }

    const onFinish = async (values) => {
        const payload = {
            user_id: userId,
            employeeName,
            type: leaveType,
            reason: values.reason,
        }

        if (leaveType === "vacation") {
            payload.startDate = values.dateRange?.[0]?.format("YYYY-MM-DD")
            payload.endDate = values.dateRange?.[1]?.format("YYYY-MM-DD")
        }
        else {
            payload.date = values.date?.format("YYYY-MM-DD")
            payload.fullDay = !halfDay

            if (halfDay) {
                payload.startTime = values.timeRange?.[0]?.format("HH:mm:ss")
                payload.endTime = values.timeRange?.[1]?.format("HH:mm:ss")
            }
        }

        console.log("Apply leave payload:", payload)

        try {
            const response = await secureApi.post(POST?.APPLY_LEAVE, payload)

            if (response?.success) {
                message.success(translations[language]?.leaveAppliedSuccess)
                form.resetFields()
                setHalfDay(false)

                if (typeof onSuccess === "function") {
                    onSuccess()
                }
            } else {
                message.error(response?.message || translations[language]?.leaveApplyError)
            }
        } catch {
            message.error(translations[language]?.leaveSubmitError)
        }
    }



    const handleCancel = () => {
        form.resetFields()
        setHalfDay(false)
        if (typeof onCancel === "function") onCancel()
    }

    return (
        <div className="leave-entry-container" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ flex: 1, overflowY: "auto", paddingBottom: 80 }}>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item label={translations[language]?.leaveTypeLabel} name="type" initialValue="vacation">
                        <Select onChange={(val) => setLeaveType(val)}>
                            <Option value="vacation">{translations[language]?.vacation}</Option>
                            <Option value="leave">{translations[language]?.leave}</Option>
                        </Select>
                    </Form.Item>

                    {leaveType === "vacation" && (
                        <Form.Item
                            label={translations[language]?.vacationDateRange}
                            name="dateRange"
                            rules={[{ required: true, message: translations[language]?.vacationDateRequired }]}
                        >
                            <RangePicker
                                disabledDate={isDateDisabled}
                                style={{ width: "100%" }}
                                placeholder={[
                                    translations[language]?.startDate,
                                    translations[language]?.endDate
                                ]}
                            />
                        </Form.Item>
                    )}

                    {leaveType === "leave" && (
                        <>
                            <Form.Item
                                label={translations[language]?.leaveDate}
                                name="date"
                                rules={[{ required: true, message: translations[language]?.leaveDateRequired }]}
                            >
                                <DatePicker
                                    disabledDate={isDateDisabled}
                                    style={{ width: "100%" }}
                                    placeholder={translations[language]?.selectDate}
                                />
                            </Form.Item>

                            <Form.Item label={translations[language]?.leaveDuration}>
                                <Select value={halfDay ? "half" : "full"} onChange={(val) => setHalfDay(val === "half")}>
                                    <Option value="full">{translations[language]?.fullDay}</Option>
                                    <Option value="half">{translations[language]?.halfDay}</Option>
                                </Select>
                            </Form.Item>

                            {halfDay && (
                                <Form.Item
                                    label={translations[language]?.timeRange}
                                    name="timeRange"
                                    rules={[{ required: true, message: translations[language]?.timeRangeRequired }]}
                                >
                                    <TimePicker.RangePicker format="HH:mm" style={{ width: "100%" }} />
                                </Form.Item>
                            )}
                        </>
                    )}

                    <Form.Item
                        label={translations[language]?.reason}
                        name="reason"
                        rules={[{ required: true, message: translations[language]?.reasonRequired }]}
                    >
                        <TextArea rows={4} placeholder={translations[language]?.reasonPlaceholder} style={{ minHeight: "100px", maxHeight: "200px", resize: "vertical" }}
                        />
                    </Form.Item>
                </Form>
            </div>

            <div
                style={{
                    borderTop: "1px solid #f0f0f0",
                    padding: "22px 0 0px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    background: "#fff",
                }}
            >
                <Button size="large" onClick={handleCancel} className="cancel-button">
                    {translations[language]?.cancel}
                </Button>
                <Button
                    type="primary"
                    onClick={() => form.submit()}
                    size="large"
                    className="submit-button"
                    icon={<Plus size={20} />}
                >
                    {translations[language]?.apply}
                </Button>
            </div>
        </div>
    )
}

export default ApplyLeave
